<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue';
import { useStore } from 'vuex';
import SimpleBar from 'simplebar-vue';

import NavBar from '@/components/nav-bar.vue';
import Menu from '@/components/menu.vue';
import RightBar from '@/components/right-bar.vue';
import Footer from '@/components/footer.vue';
import WatermarkOverlay from '@/components/WatermarkOverlay.vue';

const store = useStore();
const scrollbar = ref(null);
const isSidebarMinified = ref(false);
const isSidebarMobileToggled = ref(false);
const layoutState = computed(() => store.state.layout);
const watermarkTraceCode = computed(
	() => store.state.settings.auth.traceCode || ''
);
// eslint-disable-next-line no-undef
const watermarkEnabled = YSS_WATERMARK_MODE === true;

const appShellClasses = computed(() => ({
	'app-sidebar-minified': isSidebarMinified.value,
	'app-sidebar-mobile-toggled': isSidebarMobileToggled.value,
	'app-sidebar-mobile-closed': !isSidebarMobileToggled.value,
	'app-content-full-width': layoutState.value.visibility === 'hidden',
}));

const applyLayoutAttributes = () => {
	const layout = layoutState.value;
	document.documentElement.setAttribute('data-layout', layout.layoutType);
	document.documentElement.setAttribute(
		'data-layout-width',
		layout.layoutWidth
	);
	document.documentElement.setAttribute(
		'data-layout-position',
		layout.position
	);
	document.documentElement.setAttribute(
		'data-sidebar-size',
		layout.sidebarSize
	);
	document.documentElement.setAttribute(
		'data-sidebar-color',
		layout.sidebarColor
	);
	document.documentElement.setAttribute('data-sidebar', layout.sidebarColor);
	document.documentElement.setAttribute(
		'data-sidebar-image',
		layout.sidebarImage
	);
	document.documentElement.setAttribute(
		'data-layout-style',
		layout.sidebarView
	);
	document.documentElement.setAttribute('data-topbar', layout.topbar);
	document.documentElement.setAttribute('data-preloader', layout.preloader);
	document.documentElement.setAttribute(
		'data-sidebar-visibility',
		layout.visibility
	);
	document.documentElement.setAttribute(
		'data-bs-theme',
		layout.mode === 'dark' ? 'dark' : 'light'
	);
};

const updateSidebarSize = () => {
	const sidebarSize = window.innerWidth < 1025 ? 'sm' : 'lg';
	document.documentElement.setAttribute('data-sidebar-size', sidebarSize);
};

const toggleMenu = () => {
	if (window.innerWidth >= 992) {
		isSidebarMinified.value = !isSidebarMinified.value;
		document.documentElement.setAttribute(
			'data-sidebar-size',
			isSidebarMinified.value ? 'sm' : 'lg'
		);
		return;
	}

	isSidebarMobileToggled.value = !isSidebarMobileToggled.value;
};

const handleResize = () => {
	document.body.classList.remove('vertical-sidebar-enable');
	updateSidebarSize();
};

const closeMobileSidebar = () => {
	isSidebarMobileToggled.value = false;
};

onMounted(() => {
	localStorage.setItem('hoverd', 'false');
	applyLayoutAttributes();
	updateSidebarSize();

	window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize);
});
</script>

<template>
	<div class="app" :class="appShellClasses">
		<NavBar @toggle-menu="toggleMenu" />
		<div id="sidebar" class="app-sidebar">
			<SimpleBar
				id="scrollbar"
				class="app-sidebar-content"
				ref="scrollbar"
			>
				<Menu />
			</SimpleBar>
			<button
				type="button"
				class="app-sidebar-mobile-backdrop"
				:aria-label="$t('topbar.navigation.close' /* 關閉導覽列 */)"
				@click="closeMobileSidebar"
			></button>
		</div>

		<div class="app-content">
			<BContainer fluid class="app-content-container">
				<slot />
			</BContainer>
		</div>
		<Footer />
		<RightBar />
		<WatermarkOverlay
			v-if="watermarkEnabled"
			:theme="layoutState.mode"
			:trace-code="watermarkTraceCode"
		/>
	</div>
</template>
